import {
  KpiSeriesKey,
  MetricKey,
  SonarMetricValues,
  SnapshotComparison,
  TimeSeriesPoint,
} from './types';

export const toNumber = (value: number | string | null | undefined) => {
  if (value === null || value === undefined || value === '') return null;
  const numberValue = Number(value);
  return Number.isFinite(numberValue) ? numberValue : null;
};

export const average = (values: Array<number | string | null | undefined>) => {
  const clean = values.map(toNumber).filter((value): value is number => value !== null);
  if (!clean.length) return null;
  return clean.reduce((sum, value) => sum + value, 0) / clean.length;
};

export const formatNumber = (value: number | null, decimals = 1) => {
  if (value === null) return '-';
  if (Math.abs(value) >= 1000) return Math.round(value).toLocaleString();
  return value.toFixed(decimals);
};

export const formatFullCount = (value: number | null) => {
  if (value === null) return '-';
  return Math.round(value).toLocaleString();
};

export const formatMetricValue = (value: number | null, metric: MetricKey) => {
  if (value === null) return '-';
  if (metric === 'coverage') return `${value.toFixed(1)}%`;
  if (metric === 'code_smells') return Math.round(value).toLocaleString();
  return value.toFixed(2);
};

export const formatDate = (value: string) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString(undefined, {
    month: 'short',
    day: '2-digit',
    year: '2-digit',
  });
};

export const formatCompactDate = (value: string) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString(undefined, { month: 'short', day: '2-digit' });
};

export const ratingLabel = (value: number | null) => {
  if (value === null || value === 0) return '-';
  if (value < 1.5) return 'A';
  if (value < 2.5) return 'B';
  if (value < 3.5) return 'C';
  if (value < 4.5) return 'D';
  return 'E';
};

export const ratingColor = (value: number | null) => {
  if (value === null || value === 0) return '#94a3b8';
  if (value < 1.5) return '#16a34a';
  if (value < 2.5) return '#65a30d';
  if (value < 3.5) return '#ca8a04';
  if (value < 4.5) return '#f97316';
  return '#dc2626';
};

export const coverageColor = (value: number | null) => {
  if (value === null) return '#94a3b8';
  if (value >= 70) return '#16a34a';
  if (value >= 50) return '#ca8a04';
  if (value > 0) return '#f97316';
  return '#dc2626';
};

export const metricColor = (value: number | null, metric: MetricKey) => {
  if (metric === 'coverage') return coverageColor(value);
  if (metric === 'code_smells') {
    if (value === null) return '#94a3b8';
    if (value > 1000) return '#dc2626';
    if (value > 250) return '#f97316';
    return '#16a34a';
  }
  return ratingColor(value);
};

export const filterByDateRange = (
  points: TimeSeriesPoint[],
  fromDate: string,
  toDate: string,
) => {
  if (!fromDate && !toDate) return points;
  const fromTime = fromDate ? new Date(`${fromDate}T00:00:00`).getTime() : null;
  const toTime = toDate ? new Date(`${toDate}T23:59:59`).getTime() : null;

  return points.filter(point => {
    const time = new Date(point.time).getTime();
    if (!Number.isFinite(time)) return true;
    if (fromTime !== null && time < fromTime) return false;
    if (toTime !== null && time > toTime) return false;
    return true;
  });
};

export const toDateInputValue = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export const daysAgoInputValue = (days: number) => {
  const date = new Date();
  date.setDate(date.getDate() - days);
  return toDateInputValue(date);
};

export const getLatestValue = (points: TimeSeriesPoint[]) => {
  const sorted = [...points]
    .filter(point => toNumber(point.value) !== null)
    .sort((a, b) => new Date(a.time).getTime() - new Date(b.time).getTime());
  return sorted.length ? toNumber(sorted[sorted.length - 1].value) : null;
};

export const aggregateSnapshotValue = (
  points: TimeSeriesPoint[],
  mode: 'average' | 'sum' | 'count',
) => {
  if (mode === 'count') return points.length ? points.length : null;

  const values = points
    .map(point => toNumber(point.value))
    .filter((value): value is number => value !== null);
  if (!values.length) return null;
  if (mode === 'sum') return values.reduce((sum, value) => sum + value, 0);
  return values.reduce((sum, value) => sum + value, 0) / values.length;
};

export const getSnapshotComparison = (
  points: TimeSeriesPoint[] = [],
  mode: 'average' | 'sum' | 'count',
): SnapshotComparison => {
  const dates = [...new Set(points.map(point => point.time))]
    .filter(Boolean)
    .sort((a, b) => new Date(a).getTime() - new Date(b).getTime());

  const latestDate = dates[dates.length - 1] || null;
  const previousDate = dates.length > 1 ? dates[dates.length - 2] : null;
  const current = latestDate
    ? aggregateSnapshotValue(points.filter(point => point.time === latestDate), mode)
    : null;
  const previous = previousDate
    ? aggregateSnapshotValue(points.filter(point => point.time === previousDate), mode)
    : null;

  return {
    current,
    previous,
    delta: current !== null && previous !== null ? current - previous : null,
    currentDate: latestDate,
    previousDate,
  };
};

export const formatTrendDelta = (delta: number, key: KpiSeriesKey) => {
  const sign = delta > 0 ? '+' : '';
  if (key === 'repo_count') return `${sign}${Math.round(delta)} repos`;
  if (key === 'coverage') return `${sign}${delta.toFixed(1)} pp`;
  if (key === 'code_smells') return `${sign}${Math.round(delta).toLocaleString()}`;
  return `${sign}${delta.toFixed(2)}`;
};

export const isPositiveTrend = (delta: number, key: KpiSeriesKey) => {
  if (key === 'repo_count') return true;
  if (key === 'coverage') return delta >= 0;
  return delta <= 0;
};

export const getDateRangeLabel = (points: TimeSeriesPoint[]) => {
  const dates = [...new Set(points.map(point => point.time))].sort(
    (a, b) => new Date(a).getTime() - new Date(b).getTime(),
  );
  if (!dates.length) return 'No dates available';
  return `${formatDate(dates[0])} to ${formatDate(dates[dates.length - 1])}`;
};

export const getRatingValues = (row: {
  reliability: number | null;
  security: number | null;
  maintainability: number | null;
}) => {
  return [toNumber(row.reliability), toNumber(row.security), toNumber(row.maintainability)]
    .filter((value): value is number => value !== null);
};

export const hasHighRiskRating = (row: SonarMetricValues) => {
  return getRatingValues(row).some(value => value >= 4);
};

export const isLowCoverageItem = (row: SonarMetricValues) => {
  const coverage = toNumber(row.coverage);
  return coverage !== null && coverage < 80;
};

export const getRiskScore = (row: SonarMetricValues) => {
  const coverage = toNumber(row.coverage);
  const reliability = toNumber(row.reliability) || 0;
  const security = toNumber(row.security) || 0;
  const maintainability = toNumber(row.maintainability) || 0;
  const smells = toNumber(row.smells) || 0;

  const coveragePenalty =
    coverage === null ? 30 :
    coverage <= 0 ? 35 :
    coverage < 40 ? 30 :
    coverage < 60 ? 20 :
    coverage < 80 ? 8 :
    0;

  const qualityPenalty = reliability * 8 + security * 10 + maintainability * 6;
  const smellPenalty = Math.min(smells / 100, 25);

  return coveragePenalty + qualityPenalty + smellPenalty;
};

export const getRiskReason = (row: SonarMetricValues) => {
  const coverage = toNumber(row.coverage);
  const smells = toNumber(row.smells) || 0;
  const reasons: string[] = [];

  if (coverage === null) reasons.push('missing coverage');
  else if (coverage < 60) reasons.push('low coverage');

  if (toNumber(row.security) !== null && Number(row.security) >= 4) reasons.push('security risk');
  if (toNumber(row.reliability) !== null && Number(row.reliability) >= 4) reasons.push('reliability risk');
  if (toNumber(row.maintainability) !== null && Number(row.maintainability) >= 4) reasons.push('maintainability risk');
  if (smells >= 1000) reasons.push('high smells');

  return reasons.length ? reasons.slice(0, 2).join(' + ') : 'highest combined score';
};